/**
 * Lightweight game analytics - tracks usage locally in localStorage
 */

const ANALYTICS_KEY = 'precision-clicker:analytics';
const MAX_SESSIONS = 100; // Keep last 100 sessions

export interface GameSession {
  id: string;
  startTime: number;
  endTime?: number;
  finalScore: number;
  finalLevel: number;
  totalClicks: number;
  perfectClicks: number;
  strikes: number;
  isNewHighScore: boolean;
}

export interface ErrorLog {
  timestamp: number;
  error: string;
  context?: string;
}

export interface AnalyticsData {
  sessions: GameSession[];
  errors: ErrorLog[];
  totalGamesPlayed: number;
  totalPlayTimeMs: number;
}

class GameAnalytics {
  private currentSession: GameSession | null = null;

  private getData(): AnalyticsData {
    try {
      const stored = localStorage.getItem(ANALYTICS_KEY);
      if (!stored) {
        return {
          sessions: [],
          errors: [],
          totalGamesPlayed: 0,
          totalPlayTimeMs: 0,
        };
      }
      return JSON.parse(stored);
    } catch {
      return {
        sessions: [],
        errors: [],
        totalGamesPlayed: 0,
        totalPlayTimeMs: 0,
      };
    }
  }

  private saveData(data: AnalyticsData): void {
    try {
      // Keep only last MAX_SESSIONS
      if (data.sessions.length > MAX_SESSIONS) {
        data.sessions = data.sessions.slice(-MAX_SESSIONS);
      }
      localStorage.setItem(ANALYTICS_KEY, JSON.stringify(data));
    } catch (err) {
      console.error('Failed to save analytics:', err);
    }
  }

  startSession(): void {
    this.currentSession = {
      id: Date.now().toString(),
      startTime: Date.now(),
      finalScore: 0,
      finalLevel: 1,
      totalClicks: 0,
      perfectClicks: 0,
      strikes: 0,
      isNewHighScore: false,
    };
  }

  endSession(finalScore: number, finalLevel: number, strikes: number, isNewHighScore: boolean): void {
    if (!this.currentSession) return;

    this.currentSession.endTime = Date.now();
    this.currentSession.finalScore = finalScore;
    this.currentSession.finalLevel = finalLevel;
    this.currentSession.strikes = strikes;
    this.currentSession.isNewHighScore = isNewHighScore;

    const data = this.getData();
    data.sessions.push(this.currentSession);
    data.totalGamesPlayed++;

    const duration = this.currentSession.endTime - this.currentSession.startTime;
    data.totalPlayTimeMs += duration;

    this.saveData(data);
    this.currentSession = null;
  }

  trackClick(isPerfect: boolean): void {
    if (!this.currentSession) return;
    this.currentSession.totalClicks++;
    if (isPerfect) {
      this.currentSession.perfectClicks++;
    }
  }

  logError(error: string, context?: string): void {
    try {
      const data = this.getData();
      data.errors.push({
        timestamp: Date.now(),
        error,
        context,
      });
      // Keep only last 50 errors
      if (data.errors.length > 50) {
        data.errors = data.errors.slice(-50);
      }
      this.saveData(data);
    } catch (err) {
      console.error('Failed to log error:', err);
    }
  }

  getStats() {
    const data = this.getData();
    const sessions = data.sessions;

    if (sessions.length === 0) {
      return {
        totalGames: 0,
        totalPlayTime: 0,
        averageScore: 0,
        averageLevel: 0,
        highScore: 0,
        accuracy: 0,
        recentErrors: [],
      };
    }

    const totalClicks = sessions.reduce((sum, s) => sum + s.totalClicks, 0);
    const totalPerfect = sessions.reduce((sum, s) => sum + s.perfectClicks, 0);

    return {
      totalGames: data.totalGamesPlayed,
      totalPlayTime: data.totalPlayTimeMs,
      averageScore: sessions.reduce((sum, s) => sum + s.finalScore, 0) / sessions.length,
      averageLevel: sessions.reduce((sum, s) => sum + s.finalLevel, 0) / sessions.length,
      highScore: Math.max(...sessions.map(s => s.finalScore)),
      accuracy: totalClicks > 0 ? (totalPerfect / totalClicks) * 100 : 0,
      recentErrors: data.errors.slice(-10),
    };
  }

  exportData(): string {
    return JSON.stringify(this.getData(), null, 2);
  }

  clearData(): void {
    localStorage.removeItem(ANALYTICS_KEY);
  }
}

export const analytics = new GameAnalytics();

// Expose to window for easy debugging
if (typeof window !== 'undefined') {
  (window as any).precisionClickerAnalytics = {
    getStats: () => analytics.getStats(),
    export: () => analytics.exportData(),
    clear: () => analytics.clearData(),
  };
}
